import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, Building2, Lock } from 'lucide-react';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { toast } from '@/components/ui/Toast';
import { useFeatureAccess } from '@onxite/plan-sdk';
import { useT } from '@/lib/i18n/useT';
import { HelpHint } from '@/features/help/HelpHint';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import type { XkBrand } from '@/lib/api/types';

/**
 * PRD Xotski v2.0 §7.3 — Settings › Brand subsection.
 *
 * Currently surfaces the Brand Inventory opt-in toggle (PRD §9, per
 * brand). The plan-level featBrandInventory flag gates whether the
 * toggle is interactive — without the flag, the toggle is read-only
 * and shows the upgrade hint.
 *
 * Per-brand toggling: each brand the user has visibility into has its
 * own row. Flipping it on enables the /co-brand-inventory endpoints
 * for that brand.
 */
export function BrandSettingsPage() {
  const t = useT();
  useFavoritable(t('settings.brand.title'));
  const qc = useQueryClient();
  const planBrandInventory = useFeatureAccess('brandInventory').enabled;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      brandsApi.update(id, { brandInventoryEnabled: enabled }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.brands.all });
      toast({ tone: 'success', title: t('settings.brand.savedToast') });
    },
    onError: () => {
      toast({ tone: 'error', title: t('settings.brand.errorToast') });
    },
  });

  const brands = data?.items ?? [];

  return (
    <div className="page space-y-6">
      <Breadcrumbs
        items={[
          { label: t('breadcrumbs.settings'), to: '/settings' },
          { label: t('settings.brand.title') },
        ]}
      />

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
          <Building2 className="h-5 w-5" />
          {t('settings.brand.title')}
          <HelpHint section="settings-brand" />
        </h1>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('settings.brand.subtitle')}
        </p>
      </header>

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <Boxes className="h-4 w-4 text-[var(--color-xk-accent)]" />
          <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">
            {t('settings.brand.inventoryTitle')}
          </h2>
          {!planBrandInventory && (
            <span className="ml-2 inline-flex items-center gap-1 rounded bg-[var(--color-bg-elevated)] px-2 py-0.5 text-[10px] font-semibold uppercase text-[var(--color-fg-muted)]">
              <Lock className="h-3 w-3" /> {t('settings.brand.lockedBadge')}
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-[var(--color-fg-muted)]">
          {t('settings.brand.inventoryDescription')}
        </p>

        <div className="mt-4 divide-y divide-[var(--color-border-subtle)]">
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : brands.length === 0 ? (
            <p className="py-4 text-sm text-[var(--color-fg-muted)]">
              {t('settings.brand.noBrands')}
            </p>
          ) : (
            brands.map((brand) => (
              <BrandToggleRow
                key={brand.id}
                brand={brand}
                interactive={planBrandInventory}
                onToggle={(enabled) =>
                  toggleMutation.mutate({ id: brand.id, enabled })
                }
                pending={toggleMutation.isPending}
              />
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

function BrandToggleRow({
  brand,
  interactive,
  onToggle,
  pending,
}: {
  brand: XkBrand;
  interactive: boolean;
  onToggle: (enabled: boolean) => void;
  pending: boolean;
}) {
  const enabled = !!brand.brandInventoryEnabled;

  return (
    <div className="flex items-center justify-between py-3">
      <div>
        <p className="text-sm font-medium text-[var(--color-fg-primary)]">
          {brand.name}
        </p>
        {brand.industry && (
          <p className="text-xs text-[var(--color-fg-muted)]">
            {brand.industry}
          </p>
        )}
      </div>
      <button
        type="button"
        aria-pressed={enabled}
        disabled={!interactive || pending}
        onClick={() => onToggle(!enabled)}
        className={[
          'relative h-6 w-11 rounded-full transition-colors',
          enabled
            ? 'bg-[var(--color-xk-accent)]'
            : 'bg-[var(--color-xk-surface-2)]',
          (!interactive || pending) && 'cursor-not-allowed opacity-60',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform',
            enabled ? 'translate-x-5' : 'translate-x-0.5',
          ].join(' ')}
        />
      </button>
    </div>
  );
}
